import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import useForm from 'assets/components/form/hooks/Form';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import TextInput from 'assets/components/inputs/text/TextInput';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import DocumentPreview from 'assets/components/other/DocumentPreview';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ClaimTypeDocument from 'models/claims/claimTypeDocument/Model.ClaimTypeDocument';
import { Fragment, useMemo } from 'react';
import { ClaimJobProps, usePublicClaimSubmitProvider } from '../../Claim.Submit.Hooks';

function OneDocForm(props: { claimTypeDocument: ClaimTypeDocument; claimJobIndex: number }) {
  const { lang } = getLocales();
  const { form } = usePublicClaimSubmitProvider();

  const claimJobs = form.data?.claim?.claimJobs || [];
  const claimJob = claimJobs[props.claimJobIndex];

  const isAlreadySubmitted = claimJob?.claimJobDocuments?.some(
    (cjd) => cjd.claimTypeDocumentId === props.claimTypeDocument.id
  );

  const docForm = useForm<{
    claimTypeDocumentId: number;
    documents: Array<Utils.FormData<Omit<Model.IClaimJobDocument, 'claimTypeDocumentId'>>>;
  }>({
    default: {
      claimTypeDocumentId: props.claimTypeDocument.id,
      documents: [],
    },
    onSubmit: async ({ claimTypeDocumentId, documents }) => {
      for (const document of documents) {
        const updatedClaimJobs = [...claimJobs];
        const currentJob = { ...updatedClaimJobs[props.claimJobIndex] };
        currentJob.claimJobDocuments = [...(currentJob.claimJobDocuments ?? []), { claimTypeDocumentId, ...document }];
        updatedClaimJobs[props.claimJobIndex] = currentJob;
        form.merge({ claim: { claimJobs: updatedClaimJobs } });
      }
      docForm.reload();
    },
    validation: (data, errors) => {
      if (data.documents?.some((it) => !it.name)) errors.documents = { name: lang.required };
      if (props.claimTypeDocument.required && !isAlreadySubmitted && data.documents?.some((it) => !it.formFile))
        errors.documents = { formFile: { _objectError: lang.required } };
    },
  });

  const uploadedFiles = useMemo(
    () =>
      docForm.data?.documents?.map((doc) => ({
        original: doc.formFile,
        type: doc.formFile.type as Utils.File.MimeType,
        dataUrl: window.URL.createObjectURL(doc.formFile),
        name: doc.name,
        notes: doc.notes,
      })),
    [docForm.data.documents]
  );

  const hasUploadedDocs = claimJob?.claimJobDocuments?.some(
    (it) => it.claimTypeDocumentId === props.claimTypeDocument.id
  );
  const allowMoreUploads = !hasUploadedDocs || !!props.claimTypeDocument.allowMultiple;
  const isDisabled = !allowMoreUploads || docForm.hasErrors;

  return (
    <Box style={{ marginBottom: Theme.Size.L }}>
      <InputWrapper>
        <FileInputSimple
          name="files"
          label={props.claimTypeDocument.name}
          value={uploadedFiles}
          onChange={({ files }) => {
            docForm.update({
              documents: [
                ...(docForm.data.documents ?? []),
                ...files.map((file) => ({
                  formFile: file.original,
                  name: file.original?.name,
                  approved: !props.claimTypeDocument.approvalRequired,
                })),
              ],
            });
          }}
          fileLimit={allowMoreUploads ? 5 : 1}
          disabled={!allowMoreUploads}
        />
        <Button
          render={lang.upload}
          onClick={docForm.submit}
          disabled={isDisabled}
          active={!docForm.hasErrors}
          class={ThemeButtonCircle}
          style={{ flexShrink: 0 }}
        />
      </InputWrapper>
      <DataMap
        data={uploadedFiles}
        render={({ data: uploadedFile, index }) => (
          <Fragment key={index}>
            <br />
            <br />
            <InputWrapper>
              <TextInput
                name="name"
                label={lang.name}
                value={uploadedFile.name}
                error={!uploadedFile.name ? lang.required : undefined}
                onChange={(data) => {
                  docForm.data.documents[index] = { ...docForm.data.documents[index], ...data };
                  docForm.update({ documents: [...docForm.data.documents] });
                }}
              />
              {props.claimTypeDocument?.allowNotes && (
                <TextInput
                  name="notes"
                  label={lang.notes}
                  value={uploadedFile.notes}
                  onChange={(data) => {
                    docForm.data.documents[index] = { ...docForm.data.documents[index], ...data };
                    docForm.update({ documents: [...docForm.data.documents] });
                  }}
                />
              )}
            </InputWrapper>
          </Fragment>
        )}
      />
    </Box>
  );
}

export default function ClaimSubmitClaimJobDocuments({ claimJob, index }: ClaimJobProps) {
  const { lang } = getLocales();
  const { form, uploadableDocuments } = usePublicClaimSubmitProvider();

  if (!uploadableDocuments?.length || !form?.data?.claim?.claimJobs?.length) return null;
  const uploadedDocIds = new Set(claimJob?.claimJobDocuments?.map((doc) => doc.claimTypeDocumentId));
  const missingRequiredDocs = uploadableDocuments.filter(
    (doc) => doc.claimTypeId === claimJob?.claimTypeId && doc.required && !uploadedDocIds.has(doc.id)
  );
  return !form.data?.claim?.claimJobs?.some((job) =>
    uploadableDocuments.some((doc) => doc.claimTypeId === job.claimTypeId)
  ) ? null : (
    <div>
      <h2>{`${lang.claim} ${lang.documents} - ${lang.job} #${index + 1}`}</h2>
      <div key={index} style={{ marginBottom: Theme.Size.S }}>
        {missingRequiredDocs.length > 0 && (
          <p
            style={{
              backgroundColor: Theme.Color.error,
              color: 'white',
              padding: `${Theme.Size.M} ${Theme.Size.L}`,
              fontWeight: 'bold',
            }}
          >
            {lang.requiredFilesForTheFollowingDocs}:
            <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
              <div style={{ marginLeft: Theme.Size.M }}>
                {missingRequiredDocs.map((doc) => (
                  <li key={doc.id}>{doc.name}</li>
                ))}
              </div>
            </ul>
          </p>
        )}
        &nbsp;
        <DataMap
          data={uploadableDocuments.filter((it) => it.claimTypeId === claimJob?.claimTypeId)}
          render={({ data: doc }) => (
            <OneDocForm key={`${index}-${doc.id}`} claimTypeDocument={doc} claimJobIndex={index} />
          )}
        />
        <DocumentPreview
          documents={claimJob?.claimJobDocuments?.map((it, docIdx) => ({
            id: it.id,
            name: it.name,
            description: it.notes,
            documentType: lang.document,
            documentName: uploadableDocuments.find(
              (ud) => ud.id === it.claimTypeDocumentId && ud.claimTypeId === claimJob?.claimTypeId
            )?.name,
            onDelete: () => {
              const updatedJobs = [...(form.data?.claim?.claimJobs ?? [])];
              const updatedDocuments = claimJob.claimJobDocuments?.filter((_, i) => i !== docIdx);
              updatedJobs[index].claimJobDocuments = updatedDocuments;
              form.merge({ claim: { claimJobs: updatedJobs } });
            },
          }))}
        />
        <br />
      </div>
    </div>
  );
}
